import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import useQueryParams from "./useQueryParams";
import * as yup from "yup";
import fp from "lodash/fp";
import { getConstantValues } from "../../constants";
import equal from "deep-equal";
import { useLocation } from "react-router";

const generateSearchSchema = (searchTypes, options = {}) => {
    const { addedSchema = {}, removeExtraParams = false } = options;
    const schema = yup.object().shape({
        ...searchTypes,
        ...addedSchema,
    });
    if (removeExtraParams) schema.noUnknown();
    return schema;
};

const searchSchemaTypes = {
    // page options
    offset: yup.number().positive(),
    limit: yup.number().positive().max(200).default(20),
    isQuickSearch: yup.bool(),
    projectId: yup.string(),
    // search options
    keyword: yup.string().default(""),
    q: yup.string(),
    folderPath: yup.string(),
    isFolderSearch: yup.bool(),
    isCategorySearch: yup.bool(),
    isNotCategoryIds: yup.bool(),
    isIncludeChildCategory: yup.bool(),
    categoryIds: yup.array(),
    // .of(yup.mixed().oneOf([yup.string(), yup.number(), yup.object()]))
    // .nullable(),
    name: yup.string(),
    id: yup.string().default(""),
    creator: yup.string(),
    status: yup
        .string()
        .transform((value, orgVal) => !fp.isNil(value) && value.toUpperCase())
        .oneOf(getConstantValues("ACTIVE_STATUS")),
    jobId: yup.string(),
    // ingestType: yup
    //     .string()
    //     .transform((value, orgVal) => !fp.isEmpty(value) && value.toUpperCase())
    //     .oneOf(getConstantValues("ASSET_INGEST_TYPES")),
    types: yup.array().of(yup.string().transform((value, orgVal) => !fp.isEmpty(value) && value.toUpperCase())),
    // extensions: yup.array().of(yup.string().transform((value) => !fp.isEmpty(value) && value.toLowerCase())),
    mediaTypes: yup.array().of(
        yup
            .string()
            .transform((value) => !fp.isEmpty(value) && value.toUpperCase())
            .oneOf(getConstantValues("ASSET_SEARCH_MEDIA_TYPES")),
    ),
    tags: yup.array().of(yup.string()),
    beginDuration: yup.string(),
    endDuration: yup.string(),
    startAt: yup.string().nullable().default(null),
    endAt: yup.string().nullable().default(null),
    createdAt: yup.string(),

    hasAssociations: yup.bool(),
    fileMeta: yup.string(),
    fileMetaTemp: yup.array().of(yup.object()),
    description: yup.string(),
    size: yup.number().positive(),
    page: yup.number().positive(),
    virtualFolderId: yup.string(),
    virtualDriveId: yup.string(),
    hierarchyType: yup.string(),
    categoryGroupId: yup.string(),
    assetCreator: yup.string(),
    assetStartAt: yup.string(),
    assetEndAt: yup.string(),
    storageClass: yup.string(),
    archiveStatus: yup
        .string()
        .transform((value, orgVal) => !fp.isNil(value) && value.toUpperCase())
        .oneOf(getConstantValues("ARCHIVED_ASSET_STATUS")),
    assetId: yup.string(), //Note: archived asset 검색시에만 사용(asset 검색은 id로 진행)
    activeView: yup.string().oneOf(["list", "hierarchy"]),
    updatedStartAt: yup.string(),
    updatedEndAt: yup.string(),
    createdStartAt: yup.string(),
    createdEndAt: yup.string(),
    updater: yup.string(),
};

// const defaultSearchSchema = yup.object().shape(searchSchemaTypes);

const useSearchQueryParams = (props) => {
    const queryParams = useQueryParams();
    const [searchParamsSchema, setSearchParamsSchema] = useState(
        generateSearchSchema(searchSchemaTypes, {
            addedSchema: fp.get("addedSchema", props),
            removeExtraParams: fp.get("removeExtraParams", props),
        }),
    );
    const [searchParams, setSearchParams] = useState({
        ...searchParamsSchema.cast(queryParams.queryParams, { stripUnknown: fp.get("removeExtraParams", props) }),
    });

    useEffect(() => {
        setSearchParamsSchema(
            generateSearchSchema(searchSchemaTypes, {
                addedSchema: fp.get("addedSchema", props),
                removeExtraParams: fp.get("removeExtraParams", props),
            }),
        );
    }, [props.addedSchema, props.removeExtraParams]);

    useEffect(() => {
        const castedSearchParams = searchParamsSchema.cast(queryParams.queryParams, {
            stripUnknown: fp.get("removeExtraParams", props),
        });

        if (!equal(searchParams, castedSearchParams)) setSearchParams({ ...castedSearchParams });
    }, [queryParams.queryParams, searchParamsSchema]);

    const pushSearchQueryParams = useCallback(
        (pushOptions = {}) => {
            const { url, params: addedParams } = pushOptions;
            const params = {
                // ...searchParams,
                ...addedParams,
            };
            queryParams.pushHistory({ url, params });
            return { ...params };
        },
        // [searchParams, queryParams, searchParamsSchema],
        [queryParams, searchParamsSchema],
    );

    const replaceSearchQueryParams = useCallback(
        (replaceOptions = {}) => {
            const { url, params: addedParams } = replaceOptions;
            const params = {
                // ...searchParams,
                ...addedParams,
            };
            queryParams.replaceHistory({ url, params });
            return { ...params };
        },
        // [searchParams, queryParams, searchParamsSchema],
        [queryParams, searchParamsSchema],
    );

    const resetSearchQueryParams = useCallback(
        (resetOptions = {}) => {
            const { action = "replace", params = {}, url } = resetOptions;
            const resetParams = searchParamsSchema.cast(
                { ...params },
                {
                    stripUnknown: fp.get("removeExtraParams", props),
                },
            );
            if (action === "push") pushSearchQueryParams({ params: resetParams, url });
            else if (action === "replace") replaceSearchQueryParams({ params: resetParams });
            else throw new Error("updateSearchQueryParams::unknown action");
            return { ...resetParams };
        },
        [queryParams, searchParamsSchema],
    );

    const updateSearchQueryParams = useCallback(
        (resetOptions = {}) => {
            const { action = "replace", params = {} } = resetOptions;
            const updateParams = searchParamsSchema.cast(
                { ...searchParams, ...params },
                {
                    stripUnknown: fp.get("removeExtraParams", props),
                },
            );

            if (action === "push") pushSearchQueryParams({ params: updateParams });
            else if (action === "replace") replaceSearchQueryParams({ params: updateParams });
            else throw new Error("updateSearchQueryParams::unknown action");
            return { ...updateParams };
        },
        [searchParams, queryParams, searchParamsSchema],
    );

    const getSearchQueryParams = useCallback(
        ({ reset, params }) => {
            let params_;
            if (reset)
                params_ = searchParamsSchema.cast(
                    { ...params },
                    {
                        stripUnknown: fp.get("removeExtraParams", props),
                    },
                );
            else
                params_ = searchParamsSchema.cast(
                    { ...searchParams },
                    {
                        stripUnknown: fp.get("removeExtraParams", props),
                    },
                );
            return { ...params_ };
        },
        [searchParams, searchParamsSchema],
    );

    const getDefaultParams = useCallback(() => {
        const defaultParams = searchParamsSchema.cast();
        return defaultParams;
    }, [searchParamsSchema]);

    const searchQueryParamsBag = {
        history: queryParams.history,
        historyAction: queryParams.action,
        params: searchParams,
        pushSearchQueryParams,
        replaceSearchQueryParams,
        resetSearchQueryParams,
        updateSearchQueryParams,
        getDefaultParams,
        getSearchQueryParams,
    };

    return searchQueryParamsBag;
};
useSearchQueryParams.propTypes = {
    addedSchema: PropTypes.object,
    removeExtraParams: PropTypes.bool,
};
useSearchQueryParams.defaultProps = {
    removeExtraParams: false,
};

export default useSearchQueryParams;
